/**
 * Outreach Sends API.
 */
import api from '../../../api';
import type {
  TestSendRequest,
  TestSendResponse,
  DryRunRequest,
  DryRunResponse,
  LiveSendRequest,
  PreviewRequest,
  PreviewResponse,
  ScheduleSendRequest,
  ScheduledSend,
  ScheduledSendListResponse,
  SendJobStatus,
  SendJobListResponse,
} from '../types';

const PREFIX = '/api/outreach/sends';

export const outreachSendsApi = {
  testSend: async (request: TestSendRequest): Promise<TestSendResponse> => {
    const { data } = await api.post<TestSendResponse>(`${PREFIX}/test`, request);
    return data;
  },

  dryRun: async (request: DryRunRequest): Promise<DryRunResponse> => {
    const { data } = await api.post<DryRunResponse>(`${PREFIX}/dry-run`, request);
    return data;
  },

  /** Start a live send – returns immediately with a job_id. */
  liveSend: async (request: LiveSendRequest): Promise<{ job_id: string; status: string }> => {
    const { data } = await api.post<{ job_id: string; status: string }>(`${PREFIX}/live`, request);
    return data;
  },

  /** Poll the progress of a running send job. */
  sendStatus: async (jobId: string): Promise<SendJobStatus> => {
    const { data } = await api.get<SendJobStatus>(`${PREFIX}/status/${jobId}`);
    return data;
  },

  /** Cancel a running send job. */
  cancelSend: async (jobId: string): Promise<{ job_id: string; status: string }> => {
    const { data } = await api.post<{ job_id: string; status: string }>(`${PREFIX}/cancel/${jobId}`);
    return data;
  },

  preview: async (request: PreviewRequest): Promise<PreviewResponse> => {
    const { data } = await api.post<PreviewResponse>(`${PREFIX}/preview`, request);
    return data;
  },

  // Scheduling
  scheduleSend: async (request: ScheduleSendRequest): Promise<ScheduledSend> => {
    const { data } = await api.post<ScheduledSend>(`${PREFIX}/schedule`, request);
    return data;
  },

  listScheduledSends: async (): Promise<ScheduledSendListResponse> => {
    const { data } = await api.get<ScheduledSendListResponse>(`${PREFIX}/scheduled`);
    return data;
  },

  cancelScheduledSend: async (sendId: string): Promise<{ id: string; status: string; message: string }> => {
    const { data } = await api.post<{ id: string; status: string; message: string }>(
      `${PREFIX}/scheduled/${sendId}/cancel`
    );
    return data;
  },

  /** List all send jobs (running, completed, failed, cancelled). */
  listJobs: async (): Promise<SendJobListResponse> => {
    const { data } = await api.get<SendJobListResponse>(`${PREFIX}/jobs`);
    return data;
  },
};
